import axios from 'axios';
import {
    CreateIntegratedRiskPictureDTO,
    IntegratedRiskPicture,
    IrpBarrierAnalysisDTO,
    IRPEvaluationResult,
    IrpNodeTabDTO,
    IrpReportLocationDTO,
    IRPReportSearchCriteria,
    IrpReportTabDTO,
    IrpReportTableRowDTO,
    IrpSkippedReportRowDTO,
    IRPStatus,
    IrpWorkbookOverviewDTO,
    PagedMappingResponse,
    ReportMappingDTO,
    ReportMappingUpdateDTO
} from '../@types/integrated-risk-picture';
import {RegisterRecommendationDTO} from '../@types/safety-recommendation';
import {API_URL, MEDIA_TYPE_V2} from '../config/constants';
import {getErrorMessage} from '../utils/utils';
import SpringDataPage, {PageInfo} from '../@types/spring-data-page';

/**
 * Service for Integrated Risk Picture operations.
 * 
 * IRP Status Workflow:
 * - DRAFT: Initial state when created, can be edited and deleted
 * - QUEUED: Ready for processing after running from DRAFT
 * - RUNNING: Currently being processed
 * - COMPLETED: Successfully processed
 * - ERROR: Processing failed, can be retried or deleted
 * - CANCELLED: Processing was cancelled
 * - ARCHIVED: Completed/Error IRPs can be archived
 */

class IrpService {
    // API URL definitions
    static integratedRiskPicturesUrlV1 = API_URL + 'integratedRiskPictures';
    static integratedRiskPicturesUrlV2 = API_URL + 'api/v2/integratedRiskPictures';
    static reportsUrlV2 = API_URL + 'api/v2/reports';

    /**
     * Transforms HATEOAS response to SpringDataPage format
     * Following the same pattern as other services (safety, group, etc.)
     * @param irpData Raw HATEOAS response from backend
     * @returns SpringDataPage with IntegratedRiskPicture content
     */
    private formIntegratedRiskPicturesPagePromise(irpData: any): Promise<SpringDataPage<IntegratedRiskPicture>> {
        const irpPage: SpringDataPage<IntegratedRiskPicture> = {
            content: [],
            page: {} as PageInfo
        };
        
        if (irpData) {
            // Extract content from HATEOAS _embedded structure
            if (irpData._embedded?.integratedRiskPictureSummaryDTOList) {
                irpPage.content = irpData._embedded.integratedRiskPictureSummaryDTOList;
            }
            
            // Extract page metadata
            if (irpData.page) {
                irpPage.page = irpData.page;
            }
        }
        
        return Promise.resolve(irpPage);
    }


    /**
     * Fetches paginated list of Integrated Risk Pictures
     * @param page Page number (0-based)
     * @param size Page size
     * @param sort Sort parameter (e.g., 'created,desc')
     * @param filters Optional filters for status and archived state
     * @returns SpringDataPage of IntegratedRiskPicture
     */
    async fetchPagedIntegratedRiskPictures(
        page: number = 0,
        size: number = 20,
        sort: string = 'created,desc',
        filters?: {
            status?: IRPStatus | 'ALL';
            archived?: 'ALL' | 'ACTIVE' | 'ARCHIVED';
        }
    ): Promise<SpringDataPage<IntegratedRiskPicture>> {
        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString(),
                sort: sort
            });

            // Add filter parameters if provided
            if (filters?.status && filters.status !== 'ALL') {
                params.append('status', filters.status);
            }
            if (filters?.archived && filters.archived !== 'ALL') {
                params.append('archived', filters.archived); // Send 'ARCHIVED' or 'ACTIVE' directly
            }

            const url = `${IrpService.integratedRiskPicturesUrlV2}?${params.toString()}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });
            
            // Transform HATEOAS response using the same pattern as other services
            return this.formIntegratedRiskPicturesPagePromise(response.data);
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view Integrated Risk Pictures');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view Integrated Risk Pictures');
            }
            
            throw new Error(`Failed to fetch Integrated Risk Pictures: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Fetches a single IRP by ID
     * @param id The ID of the IRP to fetch
     * @returns The IntegratedRiskPicture or null if not found
     */
    async getIntegratedRiskPictureById(id: number): Promise<IntegratedRiskPicture | null> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${id}`;
            const response = await axios.get<IntegratedRiskPicture>(url, {
                withCredentials: true,
                headers: { 
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle 404 as null (not found)
            if (err.response?.status === 404) {
                return null;
            }
            
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to view Integrated Risk Picture ${id}`);
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view this Integrated Risk Picture');
            }
            
            throw new Error(`Failed to get Integrated Risk Picture ${id}: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Creates a new Integrated Risk Picture
     * @param groupId The ID of the group to create the IRP in
     * @param dto The IRP creation request data
     * @returns The created IntegratedRiskPicture
     */
    async createIntegratedRiskPicture(groupId: number, dto: CreateIntegratedRiskPictureDTO): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${groupId}`;
            const response = await axios.post<IntegratedRiskPicture>(url, dto, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to create Integrated Risk Pictures in group ${groupId}`);
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to create an Integrated Risk Picture');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid request: ${getErrorMessage(err)}`);
            }
            
            throw new Error(`Failed to create Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Runs an IRP by transitioning it from DRAFT to QUEUED status
     * @param id The ID of the IRP to run
     * @returns The updated IntegratedRiskPicture with QUEUED status
     */
    async runIntegratedRiskPicture(id: number): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${id}/run`;
            const response = await axios.post<IntegratedRiskPicture>(url, {}, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to run Integrated Risk Picture ${id}`);
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to run an Integrated Risk Picture');
            }
            if (err.response?.status === 404) {
                throw new Error(`Integrated Risk Picture ${id} not found`);
            }
            if (err.response?.status === 400) {
                throw new Error(getErrorMessage(err));
            }
            
            throw new Error(`Failed to run Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Updates a DRAFT IRP (name, description, and report search criteria)
     * Only IRPs in DRAFT status can be updated
     * @param id The ID of the IRP to update
     * @param updates The fields to update
     * @returns The updated IntegratedRiskPicture
     */
    async updateDraftIntegratedRiskPicture(
        id: number, 
        updates: CreateIntegratedRiskPictureDTO 
    ): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${id}`;
            const response = await axios.put<IntegratedRiskPicture>(url, updates, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to update Integrated Risk Picture ${id}`);
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to update an Integrated Risk Picture');
            }
            if (err.response?.status === 404) {
                throw new Error(`Integrated Risk Picture ${id} not found`);
            }
            if (err.response?.status === 400) {
                const errorMsg = getErrorMessage(err);
                if (errorMsg.includes('DRAFT')) {
                    throw new Error('Only DRAFT IRPs can be updated');
                }
                throw new Error(`Invalid update request: ${errorMsg}`);
            }
            
            throw new Error(`Failed to update Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    async updateIRPMetadata(id: number, name: string, description: string): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${id}/metadata`;
            const response = await axios.patch<IntegratedRiskPicture>(url, { name, description }, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to rename Integrated Risk Picture ${id}`);
            }
            if (err.response?.status === 404) {
                throw new Error(`Integrated Risk Picture ${id} not found`);
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid request: ${getErrorMessage(err)}`);
            }
            throw new Error(`Failed to update Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Deletes an IRP
     * - DRAFT/ERROR/CANCELLED: Deletes the IRP and all associated data
     * - QUEUED/RUNNING: Cancels the job first, then deletes
     * - COMPLETED: Archives the IRP (soft delete)
     * @param id The ID of the IRP to delete
     * @returns Promise that resolves when deletion is complete
     */
    async deleteIntegratedRiskPicture(id: number): Promise<void> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${id}`;
            await axios.delete(url, {
                withCredentials: true,
                headers: { 
                    'Accept': MEDIA_TYPE_V2
                }
            });
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to delete an Integrated Risk Picture');
            }
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to delete Integrated Risk Picture ${id}`);
            }
            if (err.response?.status === 404) {
                throw new Error(`Integrated Risk Picture ${id} not found`);
            }
            if (err.response?.status === 409) {
                throw new Error('Cannot delete IRP: It was modified by another user. Please refresh and try again');
            }
            if (err.response?.status === 400) {
                throw new Error(`Cannot delete IRP: ${getErrorMessage(err)}`);
            }
            
            throw new Error(`Failed to delete Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Exports IRP results in CSV format
     * Can only export IRPs that have been completed
     * @param id The ID of the IRP to export
     * @returns Blob containing the CSV data
     * @throws Error if IRP is not in COMPLETED status or endpoint not implemented
     */
    async exportIntegratedRiskPicture(_id: number): Promise<Blob> {
        // TODO: Implement when backend endpoint is available
        throw new Error('Export Integrated Risk Picture is not yet implemented. This feature is coming soon.');
    }

    /**
     * Archives a completed or errored IRP
     * Only IRPs in COMPLETED or ERROR status can be archived
     * @param id The ID of the IRP to archive
     * @returns Promise that resolves when archiving is complete
     * @throws Error if IRP cannot be archived or endpoint not implemented
     */
    async archiveIntegratedRiskPicture(_id: number): Promise<void> {
        // TODO: Implement when backend endpoint is available
        throw new Error('Archive Integrated Risk Picture is not yet implemented. This feature is coming soon.');
    }

    /**
     * Restores an archived IRP back to its previous state
     * Removes the archived timestamp and makes the IRP active again
     * @param id The ID of the archived IRP to restore
     * @returns Promise that resolves when restoration is complete
     * @throws Error if IRP cannot be restored or endpoint not implemented
     */
    async restoreIntegratedRiskPicture(_id: number): Promise<void> {
        // TODO: Implement when backend endpoint is available
        throw new Error('Restore Integrated Risk Picture is not yet implemented. This feature is coming soon.');
    }

    /**
     * Evaluates processing requirements for a new IRP
     * @param eventSequenceId The event sequence ID to evaluate
     * @param reportCriteria The report search criteria
     * @returns Evaluation results including estimated processing time and report counts
     */
    async evaluateIRP(eventSequenceId: number, reportCriteria: IRPReportSearchCriteria): Promise<IRPEvaluationResult> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/evaluate`;
            const response = await axios.post<IRPEvaluationResult>(url, {
                eventSequenceId,
                reportSearchCriteria: reportCriteria
            }, {
                withCredentials: true,
                headers: { 
                    'Content-Type': MEDIA_TYPE_V2,
                    'Accept': MEDIA_TYPE_V2
                }
            });
            return response.data;
        } catch (err: any) {
            // Provide specific error messages based on status
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to evaluate Integrated Risk Pictures');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to evaluate an Integrated Risk Picture');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid evaluation parameters: ${getErrorMessage(err)}`);
            }
            
            throw new Error(`Failed to evaluate Integrated Risk Picture: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Fetches paginated IRP mappings with overall statistics
     * @param irpId The ID of the IRP
     * @param page Page number (0-based)
     * @param size Page size (default 20)
     * @param filter Optional filter string for report IDs or names
     * @param nodeId Optional filter to extract only reports mapped to a specific node
     * @param nodeType Optional filter to specify the type of node (EVENT or FAULT_TREE_NODE)
     * @param startDate Optional start date to filter mappings based on date of incident 
     * @param endDate Optional end date to filter mappings based on date of incident
     * @returns Paginated mapping response with statistics
     */
    async getIrpMappings(
        irpId: number, 
        page: number = 0, 
        size: number = 20, 
        filter?: string,
        nodeId?: number,
        nodeType?: 'EVENT' | 'FAULT_TREE_NODE',
        startDate?: string | null,
        endDate?: string | null
    ): Promise<PagedMappingResponse> {
        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: page.toString(),
                size: size.toString()
            });
            
            if (filter) {
                params.append('filter', filter);
            }
            if (nodeId && nodeType) {
                params.append('nodeId', nodeId.toString());
                params.append('nodeType', nodeType);
            }
            if (startDate) {
                params.append('startDate', startDate);
            }
            if (endDate) {
                params.append('endDate', endDate);
            }

            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/mappings?${params.toString()}`;
            const response = await axios.get<PagedMappingResponse>(url, {
                withCredentials: true,
                headers: { 
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`Integrated Risk Picture ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error(`Access denied: You do not have permission to view mappings for IRP ${irpId}`);
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view IRP mappings');
            }
            
            throw new Error(`Failed to get IRP mappings: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Fetches detailed mappings for a specific report in an IRP
     * @param irpId The ID of the IRP
     * @param reportProcessingId The ID of the report processing record
     * @returns Detailed report mapping including all node mappings
     */
    async getReportMappings(irpId: number, reportProcessingId: number): Promise<ReportMappingDTO> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/reports/${reportProcessingId}/mappings`;
            const response = await axios.get<ReportMappingDTO>(url, {
                withCredentials: true,
                headers: { 
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`Report mappings not found for IRP ${irpId} and report ${reportProcessingId}`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view these report mappings');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view report mappings');
            }
            
            throw new Error(`Failed to get report mappings: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Fetches detailed mappings for a specific report in an IRP
     * @param irpId The ID of the IRP
     * @param reportProcessingId The ID of the report processing record
     * @returns Detailed report mapping including all node mappings
     */
    async getReportMappingsByReportId(irpId: number, reportId: number): Promise<ReportMappingDTO> {
        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: '0',
                size: '10',
                reportId: reportId.toString()
            });
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/mappings?${params.toString()}`;
            const response = await axios.get<PagedMappingResponse>(url, {
                withCredentials: true,
                headers: { 
                    'Accept': 'application/json'
                }
            });
            let mapped = response.data.content.find(m => m.processingStatus === 'MAPPED');
            // eslint-disable-next-line no-console
            console.log('mappings from the server:', response.data.content);
            return mapped || response.data.content[0];
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`Report mappings not found for IRP ${irpId} and report ${reportId}`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view these report mappings');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view report mappings');
            }
            
            throw new Error(`Failed to get report mappings: ${getErrorMessage(err)}`);
        }
    }


    /**
     * Fetches detailed mappings for multiple reports in batch requests.
     * Automatically chunks large requests into batches of 100 to comply with backend limits.
     * All chunks are fetched in parallel for optimal performance.
     *
     * @param irpId The ID of the IRP
     * @param reportProcessingIds Array of report processing IDs to fetch
     * @returns Array of detailed report mappings including all node mappings
     * @throws Error if IRP not found, or any report not found
     */
    async getReportMappingsBatch(irpId: number, reportProcessingIds: number[]): Promise<ReportMappingDTO[]> {
        const BATCH_SIZE = 100;

        // If within limit, make a single request
        if (reportProcessingIds.length <= BATCH_SIZE) {
            return this.fetchBatchMappings(irpId, reportProcessingIds);
        }

        // Chunk into batches of 100 and fetch in parallel
        const chunks: number[][] = [];
        for (let i = 0; i < reportProcessingIds.length; i += BATCH_SIZE) {
            chunks.push(reportProcessingIds.slice(i, i + BATCH_SIZE));
        }

        const batchResults = await Promise.all(
            chunks.map(chunk => this.fetchBatchMappings(irpId, chunk))
        );

        // Flatten all results into a single array
        return batchResults.flat();
    }

    /**
     * Internal method to fetch a single batch of mappings (max 100).
     */
    private async fetchBatchMappings(irpId: number, reportProcessingIds: number[]): Promise<ReportMappingDTO[]> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/mappings/batch`;
            const response = await axios.post<ReportMappingDTO[]>(url, reportProcessingIds, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 400) {
                throw new Error(`Invalid batch request: ${getErrorMessage(err)}`);
            } else if (err.response?.status === 404) {
                throw new Error(`IRP or reports not found: ${getErrorMessage(err)}`);
            } else if (err.response?.status === 403) {
                throw new Error(`Access denied to IRP: ${getErrorMessage(err)}`);
            } else if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view report mappings');
            }
            throw new Error(`Failed to get batch report mappings: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Get all IRPs linked to a specific event sequence
     * @param eventSequenceId The ID of the event sequence
     * @returns Array of IntegratedRiskPicture linked to the event sequence
     */
    async getLinkedIrpsForEventSequence(eventSequenceId: number): Promise<Array<IntegratedRiskPicture>> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/event-sequences/${eventSequenceId}`;
            const response = await axios.get<Array<IntegratedRiskPicture>>(url, {
                withCredentials: true,
                headers: { 
                    'Accept': 'application/json'
                }
            });
            // eslint-disable-next-line no-console
            console.log('Fetched linked IRPs for event sequence', eventSequenceId, response.data);
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view Integrated Risk Pictures');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to view Integrated Risk Pictures');
            }
            throw new Error(`Failed to get Integrated Risk Pictures: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Replace reoprt mappings for a specific report in an IRP
     * @param irpId The ID of the IRP
     * @param reportProcessingId The ID of the report processing record
     */
    async replaceReportMappings(irpId: number, reportProcessingId: number, mappings: ReportMappingUpdateDTO): Promise<void> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/reports/${reportProcessingId}/mappings`;
            await axios.put<void>(url, mappings, {
                withCredentials: true,
                headers: { 
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`Report mappings not found for IRP ${irpId} and report ${reportProcessingId}`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to update these report mappings');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to update report mappings');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid update request: ${getErrorMessage(err)}`);
            }
            
            throw new Error(`Failed to update report mappings: ${getErrorMessage(err)}`);
        }
    }  
    
    /**
     * Link a report to an IRP for processing
     * @param irpId The ID of the IRP
     * @param eventSequenceId The event sequence ID associated with the report
     * @param reportId The ID of the report to link 
     */
    async linkReportToIrp(irpId: number, eventSequenceId: number, reportId: number): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/link/${eventSequenceId}/report/${reportId}`;
            const response = await axios.put(url, {}, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} or report ${reportId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to link this report');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to link this report');
            }
            throw new Error(`Failed to link report to IRP: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Unlink a report from an IRP
     * @param irpId The ID of the IRP
     * @param eventSequenceId The ID of the event sequence
     * @param reportId The ID of the report to unlink
     * @returns Promise that resolves when unlinking is complete
     * @throws Error if unlinking fails
     */
    async unlinkReportFromIrp(irpId: number, eventSequenceId: number, reportId: number): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/unlink/${eventSequenceId}/report/${reportId}`;
            const response = await axios.put(url, {}, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} or report ${reportId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to unlink this report');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to unlink this report');
            }
            throw new Error(`Failed to unlink report from IRP: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Previews the count of reports that match the given criteria
     * Used to provide real-time feedback on how many reports will be analyzed
     * @param criteria Report search criteria including filters and limit
     * @returns The count of reports matching criteria (capped by limit if specified)
     */
    async previewReportCount(criteria: IRPReportSearchCriteria): Promise<number> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/preview-report-count`;
            const response = await axios.post<number>(url, criteria, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            // Handle specific error cases
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to preview report counts');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to preview report counts');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid criteria: ${getErrorMessage(err)}`);
            }

            throw new Error(`Failed to preview report count: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Restores an archived IRP back to COMPLETED status
     * Only super users can restore archived IRPs
     * @param irpId The ID of the IRP to restore
     * @returns The restored IRP
     * @throws Error if IRP not found, access denied, or not archived
     */
    async restoreIRP(irpId: number): Promise<IntegratedRiskPicture> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/restore`;
            const response = await axios.post<IntegratedRiskPicture>(url, {}, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: Only super users can restore archived IRPs');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to restore IRPs');
            }
            if (err.response?.status === 400) {
                throw new Error(`Cannot restore: ${getErrorMessage(err)}`);
            }
            throw new Error(`Failed to restore IRP: ${getErrorMessage(err)}`);
        }
    }

    /**
     * Permanently deletes an archived IRP and all related data
     * Only super users can hard delete archived IRPs
     * WARNING: This action cannot be undone
     * @param irpId The ID of the IRP to permanently delete
     * @throws Error if IRP not found, access denied, or not archived
     */
    async hardDeleteIRP(irpId: number): Promise<void> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/hard-delete`;
            await axios.delete(url, {
                withCredentials: true,
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                }
            });
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: Only super users can permanently delete IRPs');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to delete IRPs');
            }
            if (err.response?.status === 400) {
                throw new Error(`Cannot delete: ${getErrorMessage(err)}`);
            }
            throw new Error(`Failed to delete IRP: ${getErrorMessage(err)}`);
        }
    }

    // ─── IRP Workbook BFF Methods ─────────────────────────────────────────────

    /**
     * Helper: converts a Date to an ISO-8601 date string (yyyy-MM-dd) expected by the backend.
     */
    private static toDateParam(date: Date): string {
        return date.toISOString().split('T')[0];
    }

    /**
     * Helper: appends optional workbook filter params to a URLSearchParams.
     */
    private static appendWorkbookFilters(
        params: URLSearchParams,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): void {
        if (dateFrom) {
            params.append('dateFrom', IrpService.toDateParam(dateFrom));
        }
        if (dateTo) {
            params.append('dateTo', IrpService.toDateParam(dateTo));
        }
        if (descriptorTermIds && descriptorTermIds.length > 0) {
            descriptorTermIds.forEach((id) => params.append('descriptorTermIds', id.toString()));
        }
        if (sourceNames && sourceNames.length > 0) {
            sourceNames.forEach((name) => params.append('sourceNames', name));
        }
    }

    /**
     * GET /{irpId}/workbook/overview
     * Returns IRP summary and per-node badge counts for the dendrogram.
     */
    async getWorkbookOverview(
        irpId: number,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<IrpWorkbookOverviewDTO> {
        try {
            const params = new URLSearchParams();
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const query = params.toString();
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/overview${query ? `?${query}` : ''}`;
            const response = await axios.get<IrpWorkbookOverviewDTO>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            throw new Error(`Failed to load workbook overview: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/overview/reports
     * Returns a paginated table of reports matching the current filter.
     */
    async getWorkbookOverviewReports(
        irpId: number,
        page: number = 0,
        size: number = 20,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<SpringDataPage<IrpReportTableRowDTO>> {
        try {
            const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/overview/reports?${params.toString()}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            const data = response.data;
            // Spring Boot 3.x nests page metadata under data.page; fall back to top-level for older format
            const pageInfo = (data.page && typeof data.page === 'object') ? data.page : data;
            return {
                content: Array.isArray(data.content) ? data.content : [],
                page: {
                    number: pageInfo.number ?? 0,
                    size: pageInfo.size ?? 0,
                    totalElements: pageInfo.totalElements ?? 0,
                    totalPages: pageInfo.totalPages ?? 0
                }
            };
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            throw new Error(`Failed to load workbook reports: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/skipped-reports
     * Returns a paginated list of NOT_APPLICABLE reports with LLM rejection explanations.
     */
    async getWorkbookSkippedReports(
        irpId: number,
        page: number = 0,
        size: number = 10,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null,
        search?: string | null
    ): Promise<SpringDataPage<IrpSkippedReportRowDTO>> {
        try {
            const params = new URLSearchParams({ page: page.toString(), size: size.toString() });
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            if (search && search.trim().length > 0) {
                params.append('search', search.trim());
            }
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/skipped-reports?${params.toString()}`;
            const response = await axios.get(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            const data = response.data;
            const pageInfo = (data.page && typeof data.page === 'object') ? data.page : data;
            return {
                content: Array.isArray(data.content) ? data.content : [],
                page: {
                    number: pageInfo.number ?? 0,
                    size: pageInfo.size ?? 0,
                    totalElements: pageInfo.totalElements ?? 0,
                    totalPages: pageInfo.totalPages ?? 0
                }
            };
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            throw new Error(`Failed to load skipped reports: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/overview/report-locations
     * Returns lightweight lat/lon/dateTime records for map and timeline display.
     */
    async getWorkbookReportLocations(
        irpId: number,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<IrpReportLocationDTO[]> {
        try {
            const params = new URLSearchParams();
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const query = params.toString();
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/overview/report-locations${query ? `?${query}` : ''}`;
            const response = await axios.get<IrpReportLocationDTO[]>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            throw new Error(`Failed to load report locations: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/node/{nodeId}?nodeType=FAULT_TREE_NODE&...
     * Returns all reports mapped to a specific fault tree node (unpaginated).
     */
    async getWorkbookNodeTab(
        irpId: number,
        nodeId: number,
        nodeType: 'EVENT' | 'FAULT_TREE_NODE',
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<IrpNodeTabDTO> {
        try {
            const params = new URLSearchParams({ nodeType });
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/node/${nodeId}?${params.toString()}`;
            const response = await axios.get<IrpNodeTabDTO>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid request: ${getErrorMessage(err)}`);
            }
            throw new Error(`Failed to load node tab data: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/barrier-analysis
     * Returns all MAPPED reports for an IRP with their FAULT_TREE_NODE mappings.
     * Date and descriptor filters are applied server-side.
     */
    async getWorkbookBarrierAnalysis(
        irpId: number,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<IrpBarrierAnalysisDTO> {
        try {
            const params = new URLSearchParams();
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const query = params.toString();
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/barrier-analysis${query ? `?${query}` : ''}`;
            const response = await axios.get<IrpBarrierAnalysisDTO>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            if (err.response?.status === 400) {
                throw new Error(`Invalid request: ${getErrorMessage(err)}`);
            }
            throw new Error(`Failed to load barrier analysis data: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/ntsb-recommendations
     * Returns the NTSB safety recommendations issued or reiterated by the NTSB cases of the IRP's
     * staged reports that match the primary filter, newest first.
     */
    async getWorkbookNtsbRecommendations(
        irpId: number,
        dateFrom?: Date | null,
        dateTo?: Date | null,
        descriptorTermIds?: number[] | null,
        sourceNames?: string[] | null
    ): Promise<RegisterRecommendationDTO[]> {
        try {
            const params = new URLSearchParams();
            IrpService.appendWorkbookFilters(params, dateFrom, dateTo, descriptorTermIds, sourceNames);
            const query = params.toString();
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/ntsb-recommendations${query ? `?${query}` : ''}`;
            const response = await axios.get<RegisterRecommendationDTO[]>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`IRP ${irpId} not found`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this IRP workbook');
            }
            throw new Error(`Failed to load NTSB recommendations: ${getErrorMessage(err)}`);
        }
    }

    /**
     * GET /{irpId}/workbook/report/{reportId}
     * Returns full report content, descriptor terms, and node mappings for the report analysis tab.
     */
    async getWorkbookReportTab(irpId: number, reportId: number): Promise<IrpReportTabDTO> {
        try {
            const url = `${IrpService.integratedRiskPicturesUrlV2}/${irpId}/workbook/report/${reportId}`;
            const response = await axios.get<IrpReportTabDTO>(url, {
                withCredentials: true,
                headers: { Accept: 'application/json' }
            });
            return response.data;
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`Report ${reportId} not found in IRP ${irpId}`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to view this report');
            }
            throw new Error(`Failed to load report tab data: ${getErrorMessage(err)}`);
        }
    }

    /**
     * DELETE /api/v2/reports/{reportId}/descriptor-terms/{descriptorTermId}
     * Unlinks a descriptor term from a report.
     *
     * NOTE: this is a global operation. The descriptor is removed from the report
     * for all users and all IRPs, not just the current workbook. Requires EDIT
     * permission on the report.
     */
    async unlinkDescriptorTermFromReport(reportId: number, descriptorTermId: number): Promise<void> {
        try {
            const url = `${IrpService.reportsUrlV2}/${reportId}/descriptor-terms/${descriptorTermId}`;
            await axios.delete(url, {
                withCredentials: true,
                headers: { 'Content-Type': 'application/json' }
            });
        } catch (err: any) {
            if (err.response?.status === 404) {
                throw new Error(`Descriptor term ${descriptorTermId} is not linked to report ${reportId}`);
            }
            if (err.response?.status === 403) {
                throw new Error('Access denied: You do not have permission to unlink descriptors from this report');
            }
            if (err.response?.status === 401) {
                throw new Error('Authentication required: Please log in to unlink this descriptor');
            }
            throw new Error(`Failed to unlink descriptor term from report: ${getErrorMessage(err)}`);
        }
    }

}

// Export singleton instance
export default new IrpService();